'use strict';

import moment from 'moment-timezone';

//set defaults
moment.tz.setDefault('Europe/Stockholm');
moment.locale('sv');

const FORMAT_TIME = 'HH:mm';

export const MONTHS = Object.freeze([
    'januari',
    'februari',
    'mars',
    'april',
    'maj',
    'juni',
    'juli',
    'augusti',
    'september',
    'oktober',
    'november',
    'december',
]);

export const MONTHS_SHORT = Object.freeze([
    'jan',
    'feb',
    'mar',
    'apr',
    'maj',
    'jun',
    'jul',
    'aug',
    'sep',
    'okt',
    'nov',
    'dec',
]);

export const WEEEKDAYS = Object.freeze([
    'måndag',
    'tisdag',
    'onsdag',
    'torsdag',
    'fredag',
    'lördag',
    'söndag',
]);

export const WEEEKDAYS_SHORT = Object.freeze(['mån', 'tis', 'ons', 'tor', 'fre', 'lör', 'sön']);

export const yesterdayFormat = (pubMoment, includeTime) =>
    'igår' + (includeTime ? ' ' + pubMoment.format(FORMAT_TIME) : '');

export const todayFormat = (pubMoment, includeTime) =>
    'idag' + (includeTime ? ' ' + pubMoment.format(FORMAT_TIME) : '');

export const tonightFormat = (pubMoment, includeTime) =>
    'ikväll' + (includeTime ? ' ' + pubMoment.format(FORMAT_TIME) : '');

export const midnightFormat = (pubMoment, includeTime) =>
    'inatt' + (includeTime ? ' ' + pubMoment.format(FORMAT_TIME) : '');

export const tomorrowFormat = (pubMoment, includeTime) =>
    'imorgon' + (includeTime ? ' ' + pubMoment.format(FORMAT_TIME) : '');

const weekdayDateMonthFormat = (pubMoment, includeTime) => {
    const dayNo = pubMoment.weekday();
    const monthNo = pubMoment.month();

    return (
        WEEEKDAYS_SHORT[dayNo] +
        ' ' +
        pubMoment.date() +
        ' ' +
        MONTHS_SHORT[monthNo] +
        (includeTime ? ' ' + pubMoment.format(FORMAT_TIME) : '')
    );
};

const dateMonthYearFormat = (pubMoment, includeTime) => {
    const monthNo = pubMoment.month();

    return (
        pubMoment.date() +
        ' ' +
        MONTHS_SHORT[monthNo] +
        ' ' +
        pubMoment.year() +
        (includeTime ? ' ' + pubMoment.format(FORMAT_TIME) : '')
    );
};

const dateMonthFormat = (pubMoment, includeTime = false) => {
    const date = pubMoment.date();
    let monthNo = pubMoment.month();

    return date + ' ' + MONTHS_SHORT[monthNo] + (includeTime ? ' ' + pubMoment.format(FORMAT_TIME) : '');
};

const beforeStartOfYear = (pubMoment, currentMoment) =>
    pubMoment.isBefore(currentMoment.clone().startOf('year'));

const beforeYesterday = (pubMoment, currentMoment) =>
    pubMoment.isBefore(currentMoment.clone().subtract(1, 'days').startOf('day'));

const beforeToday = (pubMoment) => pubMoment.isBefore(moment('00.00', FORMAT_TIME));

const today = (pubMoment) => {
    const dayStart = moment('23.59', FORMAT_TIME).add(-1, 'days');
    const dayEnd = moment('18.00', FORMAT_TIME);

    return pubMoment.isBetween(dayStart, dayEnd);
};

const tonight = (pubMoment) => {
    const eveningStart = moment('17.59', FORMAT_TIME);
    const eveningEnd = moment('00.00', FORMAT_TIME).add(1, 'days');

    return pubMoment.isBetween(eveningStart, eveningEnd);
};

const midnight = (pubMoment) => {
    const midnightStart = moment('23.59', FORMAT_TIME);
    const midnightEnd = moment('05.00', FORMAT_TIME).add(1, 'days');

    return pubMoment.isBetween(midnightStart, midnightEnd);
};

const tomorrow = (pubMoment) => {
    const tomorrowStart = moment('04.59', FORMAT_TIME).add(1, 'days');
    const tomorrowEnd = moment('00.00', FORMAT_TIME).add(2, 'days');

    return pubMoment.isBetween(tomorrowStart, tomorrowEnd);
};

const beforeEndOfYear = (pubMoment, currentMoment) =>
    pubMoment.isBefore(currentMoment.clone().add(1, 'years').startOf('year'));

const isValidString = (str) => str && typeof str === 'string';
const isValidISODateString = (dateStr) =>
    isValidString(dateStr) && moment(dateStr, moment.ISO_8601, true).isValid();

const splitSeconds = (seconds) => {
    const secondsPart = seconds % 60;
    const minutePart = Math.floor((seconds % 3600) / 60);
    const hourPart = Math.floor(seconds / 3600);

    return {
        seconds: secondsPart,
        minutes: minutePart,
        hours: hourPart,
    };
};

function _pad(num, size) {
    return ('000000000' + num).substr(-size);
}

export function now() {
    return moment();
}

export function isInFuture(date) {
    return moment(date) > moment();
}

export function formatDuration(seconds) {
    if (!seconds || seconds <= 0) {
        return '0 sek';
    }

    const format = [];

    // Hide seconds after 5 minutes
    if (seconds > 300) {
        const mod = seconds % 60;
        seconds = mod >= 30 ? seconds + (60 - mod) : seconds - mod;
    }

    const duration = splitSeconds(seconds);

    if (duration.hours > 0) {
        // Hide minutes after 8 hours
        if (duration.hours >= 8) {
            duration.hours = duration.minutes >= 30 ? duration.hours + 1 : duration.hours;
            duration.minutes = 0;
        }

        format.push(duration.hours);
        format.push('tim');
    }

    if (duration.minutes > 0) {
        format.push(duration.minutes);
        format.push('min');
    }

    if (duration.seconds > 0) {
        format.push(duration.seconds);
        format.push('sek');
    }

    return format.join(' ');
}

/**
 * Format duration like MM:SS when less than an hour or HH:MM:SS when longer than an hour
 * @param {Number} durationInSeconds a number of seconds
 * @returns {string} the formatted duration
 */
export function formatCompactDuration(durationInSeconds) {
    let hours = Math.floor(durationInSeconds / 60 / 60);
    let minutes = Math.floor(durationInSeconds / 60) % 60;
    let seconds = durationInSeconds % 60;

    return (hours > 0 ? hours + ':' : '') + _pad(minutes, 2) + ':' + _pad(seconds, 2);
}

//
// Format a ISO date string to the format 'longday date longmonth year'
//
export function formatLongDate(dateStr) {
    if (!isValidISODateString(dateStr)) {
        return '';
    }

    const dateMoment = moment(dateStr);
    const dayNo = dateMoment.weekday();
    const monthNo = dateMoment.month();

    return WEEEKDAYS[dayNo] + ' ' + dateMoment.date() + ' ' + MONTHS[monthNo] + ' ' + dateMoment.year();
}

/**
 * Format the publication data accordingly to the SVT date format specification.
 *
 * @param {IsoString} publicationDate the date to format
 * @param {Boolean} includeTime (optinal) to include time in formated string. defaults to false
 * @return {String} the date in its string format
 */
export function formatPublicationDate(publicationDate, includeTime = true) {
    if (!isValidISODateString(publicationDate)) {
        return '';
    }

    const pubMoment = moment(publicationDate);
    const currentMoment = moment();

    if (beforeStartOfYear(pubMoment, currentMoment)) {
        return dateMonthYearFormat(pubMoment, includeTime);
    } else if (beforeYesterday(pubMoment, currentMoment)) {
        return weekdayDateMonthFormat(pubMoment, includeTime);
    } else if (beforeToday(pubMoment)) {
        return yesterdayFormat(pubMoment, includeTime);
    } else if (today(pubMoment)) {
        return todayFormat(pubMoment, includeTime);
    } else if (tonight(pubMoment)) {
        return tonightFormat(pubMoment, includeTime);
    } else if (midnight(pubMoment)) {
        return midnightFormat(pubMoment, includeTime);
    } else if (tomorrow(pubMoment)) {
        return tomorrowFormat(pubMoment, includeTime);
    } else if (beforeEndOfYear(pubMoment, currentMoment)) {
        return weekdayDateMonthFormat(pubMoment, includeTime);
    }

    return dateMonthYearFormat(pubMoment, includeTime);
}

/**
 * Format the date accordingly to number of days to date. Ex 6 days into
 * the future becomes '6 dagar'
 *
 * @param {IsoString} date the date to format
 * @param {Date} currentMoment (optinal) date instance, for testing
 * @return {String} the date in its string format
 */
export function formatDurationFromToday(date) {
    const laterMoment = moment(date);
    const currentMoment = now();

    //if more than a day difference, count day from start of day
    if (laterMoment.diff(currentMoment, 'hour') >= 24) {
        laterMoment.startOf('day');
        currentMoment.startOf('day');
    }

    return laterMoment.from(currentMoment, true).replace('en', '1');
}

/**
 * Format timestamp for live according to svt style guide
 *
 * @param {IsoString} broadcastStartTime the date to format
 * @param {Boolean} capitalized should be capitalized
 * @return {String} the date in its string format
 */
export function formatDayOrDate(broadcastStartTime) {
    let startMoment = moment(broadcastStartTime);

    if (today(startMoment)) {
        //idag
        return todayFormat(startMoment);
    } else if (tonight(startMoment)) {
        //ikväll
        return tonightFormat(startMoment);
    } else if (midnight(startMoment)) {
        //inatt
        return midnightFormat(startMoment);
    } else if (tomorrow(startMoment)) {
        //imorgon
        return tomorrowFormat(startMoment);
    }

    //22 feb
    return dateMonthFormat(startMoment);
}

/**
 * Format timestamp into a human readable time in the format '08.15'
 *
 * @param {IsoString} broadcastStartTime the date object to format
 * @returns {String} the time
 */
export function getStartTime(broadcastStartTime) {
    return moment(broadcastStartTime).format(FORMAT_TIME);
}

export function broadcastStarted(broadcastStartTime) {
    return broadcastStartTime ? now() > moment(broadcastStartTime) : false;
}

export function broadcastEnded(broadcastEndTime) {
    if (!broadcastEndTime) {
        return false;
    }

    return broadcastEndTime ? now() > moment(broadcastEndTime) : false;
}

export function broadcastedNow(broadcastStartTime, broadcastEndTime) {
    if (!broadcastStartTime || !broadcastEndTime) {
        return false;
    }

    return broadcastStarted(broadcastStartTime) && !broadcastEnded(broadcastEndTime);
}

export function upcomingBroadcast(startTime) {
    return startTime ? now() < moment(startTime) : false;
}

export function formatIsoDuration(seconds) {
    const duration = moment.duration({ seconds: seconds });

    return (
        'P' +
        (duration.years() ? duration.years() + 'Y' : '') +
        (duration.months() ? duration.months() + 'M' : '') +
        (duration.days() ? duration.days() + 'D' : '') +
        (duration.hours() || duration.minutes() || duration.seconds() ? 'T' : '') +
        (duration.hours() ? duration.hours() + 'H' : '') +
        (duration.minutes() ? duration.minutes() + 'M' : '') +
        (duration.seconds() ? duration.seconds() + 'S' : '')
    );
}

export { moment };
